import React, { useState } from "react";
import { defineMessages, injectIntl } from "react-intl";
import { Query } from "react-apollo";
import { getIn } from "final-form";
import { arrayDifference, localize } from "cci-utility";
import SelectField from "./SelectField";

const messages = defineMessages({
  new: {
    id: "new",
    defaultMessage: "New..."
  }
});
/**
 * A variation of the SelectField component that a GraphQL query, queries the server and renders the options.
 *
 * Props:
 * - query: Apollo query that must be executed to obtain the options that will be shown in the select field.
 * - variables: Variables that must be passed down to the query before sending to the server. This object is concatenated to the actual variables:
 *   - { name: "", startIndex: 0, stopIndex: 100 }
 * - value: Value record whose valueKey will be used to determine the selected record.
 * - valueKey: Property name in the record that contains the unique identifier for the record. Defaults to "id".
 * - labelKey: Property name in the record that contains the label that the user will see to select the record. Defaults to "name".
 * - displayLabel: Function that returns the text that the user will see to select the record. If this prop is not used labelKey Prop will be used. This overrides labelKey.
 * - labelSearch: Function that returns the text that the variables object will use on the 'searchVariable' property. If this prop is not used value[labelKey] text or searchText state will be used.
 * - valueOnly: Indicates if only the value should be set in the field. Defaults to false.
 * - excludeValues: Indicates values to not be shown in the select field. This is an object with the following properties:
 *   - values: The array of values to not be shown (elements of the array must be the same form of the items in the select field).
 *   - equalsFn: Function used to determine when 2 items are equals.
 * - enableRefresh:
 * - enableNew: Enables to add a new item from the server.
 * - enableServerSearch: This enables the the query by the "name" property on actual variables,
 * - searchVariable: When enableServerSearch === true, this is the variable which contains the text inserted in order to make the search. Default = "name",
 * - fetchPolicy: Apollo fetchPolicy. Default is "cache-and-network",
 * - onQueryCompleted: Function to lunch when query has been completed.,
 */
const SelectRecordField = ({
  intl,
  query,
  skip,
  variables,
  value = {},
  valueKey,
  labelKey,
  displayLabel,
  labelSearch,
  valueOnly,
  onChange,
  excludeValues,
  enableRefresh,
  enableNew,
  newLabel,
  enableServerSearch,
  searchVariable = "name",
  fetchPolicy = "cache-and-network",
  onQueryCompleted = () => {},
  onInputChange = () => {},
  onBlur = () => {},
  ...restProps
}) => {
  const [searchText, setSearchText] = useState("");

  return (
    // TODO Might be best to look into the react-select Promise option so that we can show when the data is loading
    <Query
      query={query}
      skip={skip || (value && value[valueKey] === "new")}
      variables={{
        [searchVariable]: searchText || labelSearch(value) || value[labelKey] || "",
        startIndex: 0,
        stopIndex: 100,
        ...variables
      }}
      fetchPolicy={fetchPolicy}
      onCompleted={onQueryCompleted}
      errorPolicy="all"
    >
      {({ loading, data = {}, refetch }) => {
        // Variable to read if an item is inactive.
        // TODO this might be handled in another way. This is a very specific thing (status) to use in this abstract component.
        let color = null;

        // Pull the data array from the query results
        const options =
          loading || !data[Object.keys(data)[0]]
            ? []
            : data[Object.keys(data)[0]].content;

        // First we filter options using excludeValues prop.
        let optionsAfterExclusion = options;
        if (enableNew) {
          if (!optionsAfterExclusion.find(opItem => opItem.id === "new")) {
            optionsAfterExclusion.push({
              id: "new",
              name: localize(intl, newLabel || messages.new)
            });
          }
        }

        if (excludeValues && excludeValues.values && excludeValues.equalsFn) {
          optionsAfterExclusion = arrayDifference(
            excludeValues.equalsFn,
            options,
            excludeValues.values
          );

          // Selected value must always be part of the final options
          if (value[valueKey]) {
            optionsAfterExclusion = [value, ...optionsAfterExclusion];
          }
        }

        // Using the valueKey and labelKey, we create the object that SelectField expects
        const optionsObjectArray = optionsAfterExclusion.map(
            (option, index) => ({
              value: getIn(option, valueKey),
              label:
                displayLabel(option) ||
                getIn(option, labelKey) ||
                getIn(option, valueKey),
              status: option.status,
              index
            })
          ),
          // Get the record that is currently selected.
          // We use null here to ensure data is set, using undefined in final form does not reset the value
          selectedOption = valueOnly
            ? value[valueKey]
            : optionsObjectArray.find(
                option => option.value === value[valueKey]
              ) || null;

        // TODO this might be handled in another way. This is a very specific thing (status) to use in this abstract component.
        color =
          selectedOption && selectedOption.status === "inactive"
            ? "grey"
            : null;

        return (
          <SelectField
            {...restProps}
            refresh={enableRefresh && refetch}
            onChange={selected => {
              if (!selected) {
                onChange(null);
              } else if (valueOnly) {
                onChange(selected.value);
              } else {
                // We use the entire record to include the version, status and any other field that might be needed.
                // Version is needed for serialization to the server, status is used for coloring certain fields.
                onChange(
                  options.find(option => option[valueKey] === selected.value)
                );
              }
            }}
            options={optionsObjectArray}
            value={selectedOption}
            color={color}
            clearable={true}
            onInputChange={e => {
              enableServerSearch && setSearchText(e);
              onInputChange(e, optionsObjectArray)
            }}
            onBlur={e => {
              // Simulating TextInput.onBlur() parameter.
              // Returning optionsObjectArray array as part of the parameter.
              if (e) {
                onBlur({ ...e, optionsObjectArray, value, nativeEvent: { ...e.nativeEvent, text: searchText } });
              } else {
                onBlur({ optionsObjectArray, value, nativeEvent: { text: searchText } });
              }
            }}
          />
        );
      }}
    </Query>
  );
};

SelectRecordField.defaultProps = {
  valueKey: "id",
  labelKey: "name",
  displayLabel: opt => {},
  labelSearch: val => {},
  valueOnly: false,
  enableRefresh: false,
  enableNew: false
};

export default injectIntl(SelectRecordField);
